/**
 * Hostname-aware gate (Sprint 921 — go-live preparation).
 *
 * When the user is visiting the production apex domain (e.g. `studioplaz.com`
 * or `www.studioplaz.com`) we want to serve the ComingSoon landing page on
 * every URL so the world sees a single email-capture page until launch.
 *
 * The full app is reachable on `app.studioplaz.com` AND on the original
 * Emergent preview URL (so internal QA never gets gated).
 *
 * Demo bypass — useful for handing prospects a clickable link before launch:
 *   • Append `?demo=<token>` to ANY URL on the apex (e.g.
 *     `studioplaz.com/?demo=letmein`). On first visit the app reads the
 *     query-param, validates it against `REACT_APP_DEMO_UNLOCK_TOKEN`, sets a
 *     `splaz_unlock=1` cookie (90-day), and lets the user through.
 *   • Once the cookie is set, every subsequent visit on the apex bypasses
 *     the gate. To revoke a prospect's access, change the env token and
 *     they're back on coming-soon next time their cookie expires.
 *
 * Configuration (frontend/.env):
 *   REACT_APP_GATE_HOSTNAMES         — CSV of apex hostnames to gate
 *                                       (default: "studioplaz.com,www.studioplaz.com")
 *   REACT_APP_DEMO_UNLOCK_TOKEN      — secret string that unlocks the gate
 *                                       (default: empty → no demo bypass)
 *
 * Exports:
 *   shouldGateToComingSoon()  → boolean
 *   processDemoUnlock(search) → string|null  (cleaned search-string after
 *                                              consuming ?demo=… or null)
 */
const COOKIE_NAME = "splaz_unlock";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 90; // 90 days

// Sprint 946I — launch_mode cache.  When the founder flips
// launch_mode=true under /platform/launch/config the apex gate must
// disappear without a redeploy.  We poll the public endpoint, cache the
// boolean in localStorage with a short TTL, and fall back to gate-on
// (the safer default) on any fetch failure.
const LAUNCH_MODE_CACHE_KEY = "splaz_launch_mode_v1";
const LAUNCH_MODE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function _readLaunchModeCache() {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(LAUNCH_MODE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.value !== "boolean") return null;
    if (typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > LAUNCH_MODE_TTL_MS) return null;
    return parsed.value;
  } catch {
    return null;
  }
}

function _writeLaunchModeCache(value) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      LAUNCH_MODE_CACHE_KEY,
      JSON.stringify({ value: !!value, ts: Date.now() })
    );
  } catch { /* quota/private mode — ignore */ }
}

/**
 * Synchronous accessor for the cached launch_mode flag.  Returns false
 * when no fresh cache exists (= safer default: keep the gate on).
 * The async refresh kicks in via refreshLaunchModeFromAPI() below; the
 * App calls it once at boot.
 */
export function isLaunchModeOn() {
  return _readLaunchModeCache() === true;
}

/**
 * Background refresh of launch_mode from the public endpoint.  Safe to
 * call repeatedly; failures are silent (cache TTL falls through).  On
 * fresh fetch we reload only if the value flipped — so a launch-day
 * toggle goes live within ~5 min for users sitting on the apex.
 *
 * Sprint 946I+ — Also tags Sentry + pushes a GA4/dataLayer event so
 * dashboards can split pre-launch waitlist conversions from post-launch
 * trial conversions without re-tagging every URL.  No external deps —
 * uses optional chaining so it's a no-op if Sentry / gtag aren't loaded.
 */
export async function refreshLaunchModeFromAPI(apiBase = "") {
  if (typeof fetch === "undefined") return;
  try {
    const url = `${apiBase || ""}/api/launch/launch-config/public`;
    const r = await fetch(url, { credentials: "omit" });
    if (!r.ok) return;
    const data = await r.json();
    if (typeof data?.launch_mode !== "boolean") return;
    const previous = _readLaunchModeCache();
    _writeLaunchModeCache(data.launch_mode);

    // Sprint 946I+ — tag every session with the current mode so any
    // event already in flight (page_view, sign_up_start, etc.) carries
    // the marketing_mode_active dimension automatically.
    try { window.Sentry?.setTag?.("marketing_mode_active", String(!!data.launch_mode)); } catch { /* noop */ }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "launch_mode_state",
        launch_mode_active: !!data.launch_mode,
      });
    } catch { /* noop */ }

    // Only auto-reload AND fire a transition event when the flag
    // actually flipped (and the user is on the gated apex).
    if (
      previous !== null
      && previous !== data.launch_mode
    ) {
      try {
        window.gtag?.("event", "launch_mode_change", {
          active: !!data.launch_mode,
          previous,
        });
        window.dataLayer?.push({
          event: "launch_mode_change",
          launch_mode_active: !!data.launch_mode,
          previous,
        });
      } catch { /* noop */ }
      if (
        typeof window !== "undefined"
        && _gatedHostnames().includes((window.location.hostname || "").toLowerCase())
      ) {
        window.location.reload();
      }
    }
  } catch { /* offline / CORS / DNS — leave cache as-is */ }
}


function _readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : null;
}


function _writeCookie(name, value, maxAge = COOKIE_MAX_AGE) {
  if (typeof document === "undefined") return;
  // SameSite=Lax + Secure — safe across modern browsers, works on HTTPS.
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax; Secure`;
}


function _gatedHostnames() {
  const csv = process.env.REACT_APP_GATE_HOSTNAMES || "studioplaz.com,www.studioplaz.com";
  return csv.split(",").map(h => h.trim().toLowerCase()).filter(Boolean);
}


/**
 * Process a `?demo=<token>` URL parameter on first load. If the token matches
 * `REACT_APP_DEMO_UNLOCK_TOKEN`, write the unlock cookie and return the URL
 * search-string with the demo param stripped. Returns `null` if no demo param
 * was present (caller can leave the URL alone).
 */
export function processDemoUnlock(search) {
  if (!search) return null;
  const params = new URLSearchParams(search);
  const provided = params.get("demo");
  if (!provided) return null;
  const expected = process.env.REACT_APP_DEMO_UNLOCK_TOKEN || "";
  if (expected && provided === expected) {
    _writeCookie(COOKIE_NAME, "1");
  }
  // Strip the demo param either way (don't expose the secret in the URL bar).
  params.delete("demo");
  const remainder = params.toString();
  return remainder ? `?${remainder}` : "";
}


/**
 * Paths that must always render their real page even on the gated apex —
 * they are follow-ups to emails we already sent (verification links, etc.)
 * or public SEO/marketing surfaces that must be indexable on the apex.
 */
const GATE_EXEMPT_PATH_PREFIXES = [
  "/waitlist/verified",
  "/launch/champions",
  // Sprint 926 — Comparison Pages Factory. Both the canonical /vs/ form and
  // the /compare/ legacy form must render publicly so search engines can
  // crawl them on studioplaz.com.
  "/vs",
  "/compare",
  // Sprint 946 — Launch Hub public surfaces. Press kit + public changelog +
  // referral leaderboard MUST render on the apex even before launch (and
  // before the apex unlock cookie is set) so press, partners, and viral
  // referrers can land on them directly.
  "/press",
  "/changelog",
  "/leaderboard",
  // Sprint 946G — Gate-status diagnostic. ALWAYS reachable on the apex so
  // you can verify whether the prod bundle has REACT_APP_DEMO_UNLOCK_TOKEN
  // baked in without needing to bypass the gate first.
  "/__gate-status",
];


/**
 * Returns true when the current hostname is on the gated list AND the
 * unlock cookie is NOT set AND the current path isn't in the exempt list.
 * Caller should render ComingSoonPage on every route in this state.
 */
export function shouldGateToComingSoon() {
  if (typeof window === "undefined") return false;
  const host = (window.location.hostname || "").toLowerCase();
  const gated = _gatedHostnames();
  if (!gated.includes(host)) return false;
  // Sprint 946I — launch_mode flip.  When the founder enables launch
  // mode under /platform/launch/config, the apex gate disappears for
  // everyone and the public site (LandingPage at /, ComingSoonPage in
  // marketing-mode at /business) renders directly.
  if (isLaunchModeOn()) return false;
  const path = (window.location.pathname || "").toLowerCase();
  if (GATE_EXEMPT_PATH_PREFIXES.some((p) => path === p || path.startsWith(p + "/"))) return false;
  return _readCookie(COOKIE_NAME) !== "1";
}
